/**
 * Guided setup — design/PROTOTYPE-SPEC.md §15.
 *
 * ## The problem this solves
 *
 * A clinic signs up and lands on a dashboard reading £0, 0 units, 0 products,
 * with no indication of what to do next. Every number on it is an empty state,
 * which is the least persuasive possible first impression of an inventory
 * product. This is the first thing a paying customer sees, and it decides
 * whether they ever come back.
 *
 * ## What it deliberately does NOT ask
 *
 * The spec's first step is notification lead time and alert toggles. There is
 * no notification settings table and no digest job to read one — those switches
 * would store nothing and control nothing. A setup wizard whose first screen is
 * inert is worse than one that starts with something real, so it is omitted
 * until the digest exists rather than faked. Same reasoning as the Account page
 * (docs/DECISIONS.md, 26 August 2026).
 *
 * The spec's quick-add catalogue is also absent: it is demo-account-only, and
 * the spec itself says that where it is empty the product step falls back to
 * name entry. For a real new clinic it is always empty, so name entry and
 * spreadsheet import are the whole of it.
 *
 * ## Skipping is a first-class outcome
 *
 * Someone evaluating the product at 11pm should be able to get to the app.
 * Skip sets the same flag as finishing — the wizard's job is to be offered
 * once, not to be compulsory — and Learn will carry the same steps for anyone
 * who wants them later.
 */
import { PLAN_LABEL, canAccess, type Plan as CorePlan } from '@inventory-tracker/core';
import { useState, type ReactNode } from 'react';
import {
  createProduct,
  importProducts,
  markOnboarded,
  saveTargets,
  updateAccount,
  type ImportRow,
  type Me,
} from '../lib/api.js';
import { readSpreadsheet, toImportRows } from './spreadsheet.js';
import { c, primaryButton, sans } from '../theme.js';
import { smallButton } from './format.js';

type StepId = 'welcome' | 'business' | 'products' | 'goal' | 'done';

interface Props {
  me: Me;
  /** Re-fetches /auth/me and the dashboard, so the app reflects what was set up. */
  onFinished: () => void;
  /** Offered on the last step — "finishing lands on the dashboard, or starts
   *  the quick tour" (design/PROTOTYPE-SPEC.md §15). */
  onStartTour: () => void;
}

export function SetupWizard({ me, onFinished, onStartTour }: Props) {
  // Targets is Premium. Asking a Starter clinic for a profit goal would be
  // collecting something they cannot then see, so the step is skipped entirely
  // rather than shown locked — a wizard is a bad place to sell.
  const hasTargets = canAccess(me.subscription.plan as CorePlan, 'targets');

  const steps: StepId[] = hasTargets
    ? ['welcome', 'business', 'products', 'goal', 'done']
    : ['welcome', 'business', 'products', 'done'];

  const [index, setIndex] = useState(0);
  const [finishing, setFinishing] = useState(false);
  const step = steps[index] ?? 'welcome';

  const next = () => setIndex((i) => Math.min(i + 1, steps.length - 1));
  const back = () => setIndex((i) => Math.max(i - 1, 0));

  async function finish(thenTour = false) {
    setFinishing(true);
    try {
      await markOnboarded();
    } catch {
      // Never trap someone in the wizard because one call failed. Worst case it
      // is offered again next login, which is a far smaller problem than a
      // customer who cannot reach the app they just paid for.
    } finally {
      onFinished();
      // After onFinished, so the wizard is gone before the spotlight appears —
      // two overlays at once reads as a glitch.
      if (thenTour) onStartTour();
    }
  }

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(20,26,22,0.55)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 20,
        zIndex: 60,
        fontFamily: sans,
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Guided setup"
        style={{
          background: c.card,
          border: `1px solid ${c.border}`,
          borderRadius: 18,
          width: '100%',
          maxWidth: 560,
          maxHeight: '90vh',
          overflowY: 'auto',
          padding: '26px 28px 24px',
          boxShadow: '0 24px 60px rgba(20,26,22,0.28)',
        }}
      >
        <Progress current={index} total={steps.length} />

        {step === 'welcome' && <Welcome me={me} onNext={next} onSkip={() => void finish()} />}
        {step === 'business' && <Business me={me} onNext={next} onBack={back} />}
        {step === 'products' && <Products onNext={next} onBack={back} />}
        {step === 'goal' && <Goal onNext={next} onBack={back} />}
        {step === 'done' && (
          <Done
            busy={finishing}
            onFinish={() => void finish()}
            onTour={() => void finish(true)}
          />
        )}
      </div>
    </div>
  );
}

function Progress({ current, total }: { current: number; total: number }) {
  return (
    <div style={{ display: 'flex', gap: 5, marginBottom: 20 }}>
      {Array.from({ length: total }, (_, i) => (
        <div
          key={i}
          style={{
            height: 3,
            flex: 1,
            borderRadius: 2,
            background: i <= current ? c.green : c.border,
            transition: 'background 0.25s ease',
          }}
        />
      ))}
    </div>
  );
}

// ------------------------------------------------------------------ steps

function Welcome({ me, onNext, onSkip }: { me: Me; onNext: () => void; onSkip: () => void }) {
  return (
    <Step
      title={`Welcome, ${me.user.name.split(' ')[0]}`}
      blurb={`Four short steps and ${me.account.clinicName} is ready to use. You can skip and do any of it later.`}
    >
      <ul
        style={{
          listStyle: 'none',
          margin: '0 0 22px',
          padding: 0,
          fontSize: 13.5,
          lineHeight: 2,
          color: c.muted,
        }}
      >
        {[
          'Confirm your business details',
          'Add your first products, by hand or from a spreadsheet',
          'Set what the month needs to bring in',
        ].map((t) => (
          <li key={t} style={{ display: 'flex', gap: 9 }}>
            <span style={{ color: c.green }}>✓</span>
            {t}
          </li>
        ))}
      </ul>
      <Actions
        primary={{ label: 'Get started', onClick: onNext }}
        secondary={{ label: 'Skip setup', onClick: onSkip }}
      />
    </Step>
  );
}

function Business({ me, onNext, onBack }: { me: Me; onNext: () => void; onBack: () => void }) {
  const [name, setName] = useState(me.account.clinicName);
  const [tagline, setTagline] = useState(me.account.tagline ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function save() {
    setBusy(true);
    setError(null);
    try {
      await updateAccount({
        clinicName: name.trim(),
        tagline: tagline.trim() === '' ? null : tagline.trim(),
      });
      onNext();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not save');
      setBusy(false);
    }
  }

  return (
    <Step
      title="Your business"
      blurb="This is what appears on exports and reports. You can change it any time on the Account page."
    >
      <Field id="setupName" label="Business name" value={name} onChange={setName} />
      <Field
        id="setupTagline"
        label="Tagline"
        value={tagline}
        onChange={setTagline}
        placeholder="Optional, e.g. Aesthetics Clinic"
      />
      {error && <Err>{error}</Err>}
      <Actions
        primary={{ label: busy ? 'Saving…' : 'Continue', onClick: () => void save(), disabled: busy || name.trim() === '' }}
        secondary={{ label: 'Back', onClick: onBack }}
      />
    </Step>
  );
}

/**
 * The step that matters. Everything else is preference; this is the one that
 * turns an empty dashboard into a working one.
 */
function Products({ onNext, onBack }: { onNext: () => void; onBack: () => void }) {
  const [names, setNames] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<string | null>(null);

  const lines = names
    .split('\n')
    .map((l) => l.trim())
    .filter((l) => l !== '');

  async function addByName() {
    // Never a dead button: say what is missing.
    if (lines.length === 0) {
      setError('Type at least one product name in the box, one per line.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      // Sequential, not Promise.all: these hit the product-limit gate, and a
      // burst would race past the cap and then fail halfway with some created.
      let made = 0;
      for (const name of lines) {
        await createProduct({ name });
        made += 1;
      }
      setResult(`Added ${made} product${made === 1 ? '' : 's'}.`);
      setNames('');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not add those');
    } finally {
      setBusy(false);
    }
  }

  async function onFile(file: File) {
    setBusy(true);
    setError(null);
    setResult(null);
    try {
      const rows: ImportRow[] = toImportRows(await readSpreadsheet(file));
      if (rows.length === 0) {
        // Same wording as Manage stock: the usual cause is a file whose first
        // row is data rather than column headings.
        setError(
          `${file.name} had no rows with a product name. Check the first row holds your column headings.`,
        );
        return;
      }
      const res = await importProducts(rows, file.name);
      // Per the spec: an inline result box per file, success or failure — not a
      // toast that vanishes before it is read.
      setResult(
        `${file.name}: ${res.imported} added` +
          (res.unitsAdded ? `, ${res.unitsAdded} units` : '') +
          (res.skipped ? `, ${res.skipped} skipped` : ''),
      );
      if (res.limitReached) {
        setError(
          `Your ${PLAN_LABEL[res.limitReached.plan]} plan holds ${res.limitReached.limit} products, ` +
            `so ${res.limitReached.notImported} did not import. Upgrade on the Account page.`,
        );
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not read that file');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Step
      title="Add your products"
      blurb="One per line, or bring your whole list in from a spreadsheet. Quantities, batches and expiry dates come later. This is just the catalogue."
    >
      <label htmlFor="setupNames" style={labelStyle}>
        Product names
      </label>
      <textarea
        id="setupNames"
        value={names}
        onChange={(e) => setNames(e.target.value)}
        rows={5}
        placeholder={'Azzalure\nProfhilo\nObagi Nu-Derm Clear'}
        style={{
          width: '100%',
          padding: '11px 13px',
          borderRadius: 12,
          border: `1px solid ${c.borderStrong}`,
          fontSize: 14,
          fontFamily: sans,
          resize: 'vertical',
          boxSizing: 'border-box',
          marginBottom: 10,
        }}
      />

      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
        <button
          type="button"
          onClick={() => void addByName()}
          disabled={busy}
          style={{ ...smallButton, height: 38, opacity: busy ? 0.5 : 1 }}
        >
          {busy ? 'Working…' : `Add ${lines.length || ''} product${lines.length === 1 ? '' : 's'}`}
        </button>

        <span style={{ fontSize: 12.5, color: c.faint }}>or</span>

        <label
          style={{ ...smallButton, height: 38, display: 'inline-flex', alignItems: 'center', cursor: 'pointer' }}
        >
          Import a spreadsheet
          <input
            type="file"
            accept=".xlsx,.xls,.csv"
            style={{ display: 'none' }}
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) void onFile(f);
              // Reset so choosing the same file twice still fires a change.
              e.target.value = '';
            }}
          />
        </label>
      </div>

      {result && <Ok>{result}</Ok>}
      {error && <Err>{error}</Err>}

      <Actions
        primary={{ label: 'Continue', onClick: onNext, disabled: busy }}
        secondary={{ label: 'Back', onClick: onBack }}
      />
    </Step>
  );
}

function Goal({ onNext, onBack }: { onNext: () => void; onBack: () => void }) {
  const [goal, setGoal] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function save() {
    const pounds = Number(goal);
    if (goal.trim() !== '' && (!Number.isFinite(pounds) || pounds < 0)) {
      setError('Enter a number, or leave it blank');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      if (goal.trim() !== '') {
        // Pounds in, pence stored — money is integer pence everywhere.
        await saveTargets({ profitGoalPence: Math.round(pounds * 100) });
      }
      onNext();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not save');
      setBusy(false);
    }
  }

  return (
    <Step
      title="What should this month make?"
      blurb="Your profit goal on top of costs. Targets uses it to show how many treatments you need. Leave it blank and set it later."
    >
      <Field
        id="setupGoal"
        label="Monthly profit goal (£)"
        value={goal}
        onChange={setGoal}
        placeholder="e.g. 4000"
        inputMode="decimal"
      />
      {error && <Err>{error}</Err>}
      <Actions
        primary={{ label: busy ? 'Saving…' : 'Continue', onClick: () => void save(), disabled: busy }}
        secondary={{ label: 'Back', onClick: onBack }}
      />
    </Step>
  );
}

function Done({
  busy,
  onFinish,
  onTour,
}: {
  busy: boolean;
  onFinish: () => void;
  onTour: () => void;
}) {
  return (
    <Step
      title="You're set up"
      blurb="Add quantities and expiry dates with “Add stock”, from any page. A two-minute tour will show you the rest."
    >
      <Actions
        primary={{ label: busy ? 'Finishing…' : 'Take the quick tour', onClick: onTour, disabled: busy }}
        secondary={{ label: 'Go straight to my dashboard', onClick: onFinish }}
      />
    </Step>
  );
}

// -------------------------------------------------------------------- bits

function Step({ title, blurb, children }: { title: string; blurb: string; children: ReactNode }) {
  return (
    <>
      <h2 style={{ fontFamily: sans, letterSpacing: '-0.03em', fontWeight: 300, fontSize: 26, margin: '0 0 8px' }}>
        {title}
      </h2>
      <p style={{ fontSize: 13.5, color: c.muted, lineHeight: 1.75, margin: '0 0 20px' }}>{blurb}</p>
      {children}
    </>
  );
}

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: 14,
  color: c.label,
  fontWeight: 500,
  marginBottom: 5,
};

function Field({
  id,
  label,
  value,
  onChange,
  placeholder,
  inputMode,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  inputMode?: 'decimal';
}) {
  return (
    <div style={{ marginBottom: 14 }}>
      <label htmlFor={id} style={labelStyle}>
        {label}
      </label>
      <input
        id={id}
        value={value}
        placeholder={placeholder}
        inputMode={inputMode}
        onChange={(e) => onChange(e.target.value)}
        style={{
          width: '100%',
          height: 44,
          padding: '0 13px',
          borderRadius: 12,
          border: `1px solid ${c.borderStrong}`,
          fontSize: 14,
          fontFamily: sans,
          boxSizing: 'border-box',
        }}
      />
    </div>
  );
}

function Ok({ children }: { children: ReactNode }) {
  return (
    <div
      style={{
        background: c.okBg,
        border: '1px solid #D3DFCE',
        color: c.success,
        borderRadius: 12,
        padding: '10px 13px',
        fontSize: 13,
        marginTop: 12,
        lineHeight: 1.6,
      }}
    >
      {children}
    </div>
  );
}

function Err({ children }: { children: ReactNode }) {
  return (
    <div
      role="alert"
      style={{
        background: c.dangerBg,
        border: `1px solid ${c.dangerBorder}`,
        color: c.danger,
        borderRadius: 12,
        padding: '10px 13px',
        fontSize: 13,
        marginTop: 12,
        lineHeight: 1.6,
      }}
    >
      {children}
    </div>
  );
}

function Actions({
  primary,
  secondary,
}: {
  primary: { label: string; onClick: () => void; disabled?: boolean };
  secondary?: { label: string; onClick: () => void };
}) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 22 }}>
      <button
        type="button"
        onClick={primary.onClick}
        disabled={primary.disabled}
        style={{
          ...primaryButton,
          width: 'auto',
          height: 44,
          padding: '0 22px',
          opacity: primary.disabled ? 0.5 : 1,
        }}
      >
        {primary.label}
      </button>
      {secondary && (
        <button
          type="button"
          onClick={secondary.onClick}
          style={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            fontSize: 13,
            fontFamily: sans,
            color: c.muted2,
            textDecoration: 'underline',
            textUnderlineOffset: 3,
          }}
        >
          {secondary.label}
        </button>
      )}
    </div>
  );
}
